// Single source of truth for RFM segment presentation — colors, labels,
// descriptions, and recommended action copy. Every page that renders a
// segment tag/card/filter reads from here so the treatment stays consistent.
//
// The segment *union* lives in `./types` (mirrored from @bbpos/ui RfmTag); this
// file just decorates it.

import type { RfmSegment } from './types';

export interface RfmSegmentMeta {
  id: RfmSegment;
  label: string;
  short: string; // used in compact UI (filter dropdowns, list cell badges)
  description: string;
  recommendedAction: string;
  // Visual tokens — all Tailwind classes from the Orio360 palette, no ad-hoc hex
  gradient: string; // e.g. "from-amber-400 via-orange-500 to-rose-500"
  tint: string; // page card tint
  darkTint: string;
  iconBg: string; // icon circle background
  chip: string; // frosted chip on dark hero backgrounds
  textAccent: string;
  // For ReportKpiRow reuse — mapped to the existing KpiTone palette
  kpiTone: 'warning' | 'success' | 'brand' | 'danger' | 'neutral';
}

export const RFM_SEGMENTS: Record<RfmSegment, RfmSegmentMeta> = {
  CHAMPION: {
    id: 'CHAMPION',
    label: 'Champion',
    short: 'Champions',
    description: 'Your top customers — most recent, frequent, and highest spending.',
    recommendedAction: 'Reward with exclusive offers and early access to new collections.',
    gradient: 'from-amber-400 via-orange-500 to-rose-500',
    tint: 'bg-amber-50',
    darkTint: 'dark:bg-amber-900/20',
    iconBg: 'bg-gradient-to-br from-amber-500 to-orange-600',
    chip: 'bg-amber-400/25 text-amber-50 ring-1 ring-amber-300/40',
    textAccent: 'text-amber-700 dark:text-amber-300',
    kpiTone: 'warning',
  },
  LOYAL: {
    id: 'LOYAL',
    label: 'Loyal',
    short: 'Loyal',
    description: 'Frequent buyers with consistent visits — your backbone.',
    recommendedAction: 'Run a referral campaign to turn them into advocates.',
    gradient: 'from-emerald-500 via-teal-500 to-success-600',
    tint: 'bg-success-50',
    darkTint: 'dark:bg-success-900/20',
    iconBg: 'bg-gradient-to-br from-success-500 to-teal-600',
    chip: 'bg-emerald-400/25 text-emerald-50 ring-1 ring-emerald-300/40',
    textAccent: 'text-success-700 dark:text-success-300',
    kpiTone: 'success',
  },
  POTENTIAL_LOYALIST: {
    id: 'POTENTIAL_LOYALIST',
    label: 'Potential Loyalist',
    short: 'Potential',
    description: 'Recent customers with room to grow. One nudge away from loyalty.',
    recommendedAction: 'Send a welcome series with a first-anniversary voucher.',
    gradient: 'from-brand-500 via-indigo-500 to-violet-600',
    tint: 'bg-brand-50',
    darkTint: 'dark:bg-brand-900/20',
    iconBg: 'bg-gradient-to-br from-brand-500 to-indigo-600',
    chip: 'bg-brand-400/25 text-brand-50 ring-1 ring-brand-300/40',
    textAccent: 'text-brand-700 dark:text-brand-300',
    kpiTone: 'brand',
  },
  AT_RISK: {
    id: 'AT_RISK',
    label: 'At Risk',
    short: 'At Risk',
    description: 'Previously high-value customers who have gone quiet recently.',
    recommendedAction: 'Win-back email with a personalised discount on their favourites.',
    gradient: 'from-warning-500 via-orange-500 to-danger-500',
    tint: 'bg-warning-50',
    darkTint: 'dark:bg-warning-900/20',
    iconBg: 'bg-gradient-to-br from-warning-500 to-orange-600',
    chip: 'bg-warning-400/25 text-warning-50 ring-1 ring-warning-300/40',
    textAccent: 'text-warning-700 dark:text-warning-300',
    kpiTone: 'danger',
  },
  INACTIVE: {
    id: 'INACTIVE',
    label: 'Inactive',
    short: 'Inactive',
    description: 'No activity for a long period. Likely churned.',
    recommendedAction: 'Final re-engagement campaign or archive from active lists.',
    gradient: 'from-neutral-400 via-neutral-500 to-neutral-600',
    tint: 'bg-neutral-100',
    darkTint: 'dark:bg-neutral-800/60',
    iconBg: 'bg-gradient-to-br from-neutral-500 to-neutral-700',
    chip: 'bg-neutral-400/25 text-neutral-50 ring-1 ring-neutral-300/40',
    textAccent: 'text-neutral-600 dark:text-neutral-300',
    kpiTone: 'neutral',
  },
};

export const RFM_SEGMENT_ORDER: RfmSegment[] = [
  'CHAMPION',
  'LOYAL',
  'POTENTIAL_LOYALIST',
  'AT_RISK',
  'INACTIVE',
];

export function segmentMeta(segment: RfmSegment | undefined): RfmSegmentMeta | undefined {
  if (!segment) return undefined;
  return RFM_SEGMENTS[segment];
}
